import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import Footer from "../../layout/Footer";
import Navbar from "../../layout/Navbar";
import { FaSignOutAlt } from "react-icons/fa";

const TutorDashboard = () => {
    const navigate = useNavigate();
    const [tutor, setTutor] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("overview");
    const [editMode, setEditMode] = useState(false);
    const [profileForm, setProfileForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        profile_image: "",
        hourly_rate: "",
        qualification: "",
        subjects: "",
        experience: "",
        availability: "",
        bio: ""
    });
    const [stats, setStats] = useState({
        totalStudents: 0,
        upcomingSessions: 0,
        completedSessions: 0,
        totalEarnings: 0,
        averageRating: 0,
        responseRate: 0
    });
    const [upcomingSessions, setUpcomingSessions] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [recentEarnings, setRecentEarnings] = useState([]);
    const [showPaymentBanner, setShowPaymentBanner] = useState(true); // State to control banner visibility

    const API_URL = "https://pastq.eddcodesgh.com/qApi";
    const API_KEY = "f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506";

    // Fetch tutor data and bookings
    useEffect(() => {
        fetchTutorData();
        fetchTutorBookings();
    }, []);

    const fetchTutorData = async () => {
        try {
            // Get tutor ID from localStorage or session
            const tutorAppId = localStorage.getItem('tutorAppId') || sessionStorage.getItem('tutorAppId');
            
            if (!tutorAppId) {
                toast.error("Please login to access dashboard");
                navigate("/tutor/login");
                return;
            }
 
            const idLookupResponse = await axios.post(
                `${API_URL}/client/tutors/_get_tutor_id.php`,
                { application_id: tutorAppId },
                {
                    headers: {
                        'Authorization': `Bearer ${API_KEY}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            let tutorId = tutorAppId; // Default to using the app ID
            
            if (idLookupResponse.data.success && idLookupResponse.data.tutor_id) {
                tutorId = idLookupResponse.data.tutor_id;
                console.log("Found tutor ID:", tutorId);
            } else {
                console.log("Using application ID as tutor ID");
            }

            // Now fetch the profile with the correct ID
            const response = await axios.get(`${API_URL}/client/tutors/_profile.php`, {
                params: {
                    id: tutorId,
                    application_id: tutorAppId // Also pass app ID for reference
                },
                headers: {
                    'Authorization': `Bearer ${API_KEY}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                const tutorData = response.data.data;
               
                setTutor(tutorData);
                setProfileForm({
                    full_name: tutorData.full_name || "",
                    email: tutorData.email || "",
                    phone: tutorData.phone || "",
                    profile_image: tutorData.profile_image || "",
                    hourly_rate: tutorData.hourly_rate || "",
                    qualification: tutorData.qualification || "",
                    subjects: tutorData.subjects || "",
                    experience: tutorData.experience || "",
                    availability: tutorData.availability || "",
                    bio: tutorData.teaching_philosophy || tutorData.bio || ""
                });
                
                // Store the actual tutor ID for future use
                localStorage.setItem('tutorId', tutorData.id || tutorId);
                
                // Check if payment is completed and show/hide banner accordingly
                if (tutorData.registration_paid && tutorData.payment_status === 'paid') {
                    setShowPaymentBanner(false);
                } else {
                    setShowPaymentBanner(true);
                }
                
            } else {
                toast.error(response.data.message || "Failed to load tutor profile");
            }
        } catch (error) {
            toast.error("Failed to load tutor profile. Please try again.");
        }
    };

    const fetchTutorBookings = async () => {
        try {
            const tutorAppId = localStorage.getItem('tutorAppId') || sessionStorage.getItem('tutorAppId');
            const response = await axios.get(`${API_URL}/client/tutors/_get_bookings.php?tutor_id=${tutorAppId}`, {
                headers: {
                    'Authorization': `Bearer ${API_KEY}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.data.success) {
                const bookingsData = response.data.data;
                
                setBookings(bookingsData);
                
                const totalStudents = [...new Set(bookingsData.map(b => b.student_id))].length;
                const upcomingSessions = bookingsData.filter(b => 
                    b.status === 'confirmed' && new Date(b.booking_date) > new Date()
                ).length;
                const completedSessions = bookingsData.filter(b => b.status === 'completed').length;
                const totalEarnings = bookingsData
                    .filter(b => b.status === 'completed')
                    .reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);
                
                setStats({
                    totalStudents,
                    upcomingSessions,
                    completedSessions,
                    totalEarnings: totalEarnings.toFixed(2),
                    averageRating: tutor?.rating || 0,
                    responseRate: 95
                });

                // Get upcoming sessions (next 7 days)
                const upcoming = bookingsData
                    .filter(b => b.status === 'confirmed' && new Date(b.booking_date) > new Date())
                    .sort((a, b) => new Date(a.booking_date) - new Date(b.booking_date))
                    .slice(0, 5);
                
                setUpcomingSessions(upcoming);

                // Get pending booking requests
                const pending = bookingsData
                    .filter(b => b.status === 'pending')
                    .slice(0, 5);
                
                setPendingRequests(pending);
                
                const earnings = bookingsData
                    .filter(b => b.status === 'completed')
                    .sort((a, b) => new Date(b.booking_date) - new Date(a.booking_date))
                    .slice(0, 5)
                    .map(b => ({
                        ...b,
                        earnings: parseFloat(b.amount) || 0
                    }));
                
                setRecentEarnings(earnings);

            } else {
                toast.error(response.data.message || "Failed to load bookings");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to load bookings. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        
        try {
            const tutorAppId = localStorage.getItem('tutorAppId') || sessionStorage.getItem('tutorAppId');
            
            const response = await axios.put(`${API_URL}/client/tutors/_update_profile.php`, 
                { id: tutorAppId, ...profileForm },
                {
                    headers: {
                        'Authorization': `Bearer ${API_KEY}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setTutor(response.data.data);
                setEditMode(false);
                toast.success("Profile updated successfully!");
            } else {
                toast.error(response.data.message || "Failed to update profile");
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            toast.error("Failed to update profile. Please try again.");
        }
    };

    const handleBookingAction = async (bookingId, action, status) => {
        try {
            const response = await axios.put(`${API_URL}/client/tutors/_update_bookings.php`, 
                { 
                    id: bookingId, 
                    status: status,
                    action_by: 'tutor'
                },
                {
                    headers: {
                        'Authorization': `Bearer ${API_KEY}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                toast.success(`Booking ${action} successfully!`);
                fetchTutorBookings(); // Refresh bookings
            } else {
                toast.error(response.data.message || `Failed to ${action} booking`);
            }
        } catch (error) {
            console.error(`Error ${action} booking:`, error);
            toast.error(`Failed to ${action} booking. Please try again.`);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'pending': { color: 'warning', text: 'Pending', icon: 'bi-clock' },
            'confirmed': { color: 'success', text: 'Confirmed', icon: 'bi-check-circle' },
            'completed': { color: 'primary', text: 'Completed', icon: 'bi-check-all' },
            'cancelled': { color: 'danger', text: 'Cancelled', icon: 'bi-x-circle' },
            'declined': { color: 'secondary', text: 'Declined', icon: 'bi-slash-circle' }
        };
        
        const config = statusConfig[status] || statusConfig.pending;
        return (
            <span className={`badge bg-${config.color} bg-opacity-10 text-${config.color} border border-${config.color} border-opacity-25`}>
                <i className={`bi ${config.icon} me-1`}></i>
                {config.text}
            </span>
        );
    };

    const formatDate = (dateString) => {
        const options = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    const formatTime = (timeString) => {
        return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const parseSubjects = (subjects) => {
        if (!subjects) return [];
        try {
            if (typeof subjects === 'string') {
                return JSON.parse(subjects);
            }
            return subjects;
        } catch (e) {
            return subjects.split(',').map(s => s.trim());
        }
    };

    const logout = () => {
        localStorage.removeItem("tutorAppId");
        localStorage.removeItem("email");
        localStorage.removeItem("phone");
        localStorage.clear();
        toast.success("You are logged out");
        navigate("/");
    };

    // Function to hide payment banner (when user clicks close or completes payment)
    const hidePaymentBanner = () => {
        setShowPaymentBanner(false);
        // You might want to save this preference in localStorage
        localStorage.setItem('hidePaymentBanner', 'true');
    };

    if (loading) {
        return (
            <>
                <Navbar />
                <div className="container py-5">
                    <div className="row justify-content-center">
                        <div className="col-md-8 text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading tutor dashboard...</p>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Navbar />
            
            <div className="container-fluid py-4 mt-5">
                <div className="row mt-5">
                    {/* Sidebar - Tutor Specific */}
                    <div className="col-lg-3 col-xl-2 mb-4 mt-4 py-2">
                        <div className="card border-0 shadow-sm h-100 sticky-top">
                            <div className="card-body">
                                <div className="text-center mb-4">
                                    <div className="position-relative d-inline-block">
                                        <img 
                                            src={`https://pastq.eddcodesgh.com/qApi/uploads/tutors/${tutor?.profile_image}`} 
                                            alt={tutor?.full_name}
                                            className="rounded-circle mb-3 border border-3 border-primary"
                                            style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                        />
                                        <span className="position-absolute top-0 start-100 translate-middle p-1 bg-success border border-light rounded-circle">
                                            <i className="bi bi-patch-check-fill text-white fs-6"></i>
                                        </span>
                                    </div>
                                    <h5 className="fw-bold mb-1">{tutor?.full_name}</h5>
                                    <p className="text-muted small mb-1">{tutor?.qualification || "Professional Tutor"}</p>
                                    <div className="rating mb-2">
                                        <i className="bi bi-star-fill text-warning"></i>
                                        <span className="fw-bold ms-1">{stats.averageRating || "4.5"}</span>
                                        <span className="text-muted ms-1">({stats.completedSessions || 0} sessions)</span>
                                    </div>
                                    <div className="mt-2">
                                        <span className="badge bg-success bg-opacity-10 text-success">
                                            <i className="bi bi-cash-coin me-1"></i>
                                            ₵{tutor?.hourly_rate || "50"}/hr
                                        </span>
                                    </div>
                                </div>

                                {/* Tutor Navigation */}
                                <nav className="nav flex-column">
                                    <button 
                                        className={`nav-links text-start ${activeTab === 'overview' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('overview')}
                                    >
                                        <i className="bi bi-speedometer2 me-2"></i>
                                        Dashboard
                                    </button>
                                    <button 
                                        className={`nav-links text-start ${activeTab === 'schedule' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('schedule')}
                                    >
                                        <i className="bi bi-calendar-week me-2"></i>
                                        Schedule
                                        {stats.upcomingSessions > 0 && (
                                            <span className="badge bg-primary float-end">{stats.upcomingSessions}</span>
                                        )}
                                    </button>
                                    <button 
                                        className={`nav-links text-start ${activeTab === 'request' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('request')}
                                    >
                                        <i className="bi bi-bell me-2"></i>
                                        Requests
                                        {pendingRequests.length > 0 && (
                                            <span className="badge bg-warning float-end">{pendingRequests.length}</span>
                                        )}
                                    </button>
                                    {/* <button 
                                        className={`nav-links text-start ${activeTab === 'students' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('students')}
                                    >
                                        <i className="bi bi-people me-2"></i>
                                        Students
                                        {stats.totalStudents > 0 && (
                                            <span className="badge bg-info float-end">{stats.totalStudents}</span>
                                        )}
                                    </button> */}
                                    <button 
                                        className={`nav-links text-start ${activeTab === 'earnings' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('earnings')}
                                    >
                                        <i className="bi bi-wallet2 me-2"></i>
                                        My Earnings
                                    </button>
                                    <button 
                                        className={`nav-links text-start ${activeTab === 'profile' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('profile')}
                                    >
                                        <i className="bi bi-gear me-2"></i>
                                        Settings
                                    </button>
                                  
                                    <button onClick={logout} className="nav-links text-start">
                                        <FaSignOutAlt className="me-2" /> 
                                        <span className="nav-text">Logout</span>
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>

                    {/* Main Content - Tutor Dashboard */}
                    <div className="col-lg-9 col-xl-10">
                        {/* Payment Status Banner - Only show if payment is not completed */}
                        {showPaymentBanner && tutor && (!tutor.registration_paid || tutor.status !== 'paid') && (
                            <div className="row mb-4">
                                <div className="col-12">
                                    <div 
                                        className="alert alert-light border border-warning shadow-sm position-relative" 
                                        style={{
                                            background: 'linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%)',
                                            borderLeft: '4px solid #ffc107'
                                        }}
                                    >
                                        {/* Close button */}
                                        <button 
                                            type="button" 
                                            className="btn-close position-absolute top-0 end-0 m-3" 
                                            aria-label="Close"
                                            onClick={hidePaymentBanner}
                                            style={{ opacity: 0.7 }}
                                        ></button>
                                        
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center">
                                                <div 
                                                    className="rounded-circle d-flex align-items-center justify-content-center me-3" 
                                                    style={{
                                                        width: '50px',
                                                        height: '50px',
                                                        background: 'rgba(255, 193, 7, 0.2)',
                                                        color: '#856404'
                                                    }}
                                                >
                                                    <i className="bi bi-credit-card fs-4"></i>
                                                </div>
                                                <div>
                                                    <h5 className="fw-bold mb-1" style={{ color: '#856404' }}>
                                                        Complete Registration Payment
                                                    </h5>
                                                    <p className="mb-0 text-muted">
                                                        Your profile is currently hidden from students. Complete payment to make your profile visible and start receiving tutoring requests.
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            <div className="d-flex align-items-center gap-1">
                                                {/* Payment Details */}
                                                <div className="text-end me-1">
                                                    <div className="fw-bold" style={{ color: '#856404' }}>GHS 26.00</div>
                                                    <small className="text-muted">Registration Fee</small>
                                                </div>
                                                
                                                {/* Payment Button/Link */}
                                                <button 
                                                    className="btn btn-warning btn-lg px-1 py-2 fw-bold d-flex align-items-center"
                                                    onClick={() => navigate('/payment')}
                                                    style={{
                                                        background: 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)',
                                                        border: 'none',
                                                        boxShadow: '0 4px 15px rgba(255, 193, 7, 0.3)'
                                                    }}
                                                >
                                                    <i className="bi bi-lock-fill me-1"></i>
                                                    Complete Payment
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {/* Additional Info Row */}
                                        <div className="row mt-3 pt-3 border-top">
                                            <div className="col-md-4">
                                                <div className="d-flex align-items-center">
                                                    <div className="me-2" style={{ color: '#ffc107' }}>
                                                        <i className="bi bi-eye-slash"></i>
                                                    </div>
                                                    <div>
                                                        <small className="fw-semibold">Profile Status</small>
                                                        <p className="mb-0 text-danger fw-bold">Hidden</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="d-flex align-items-center">
                                                    <div className="me-2" style={{ color: '#ffc107' }}>
                                                        <i className="bi bi-people"></i>
                                                    </div>
                                                    <div>
                                                        <small className="fw-semibold">Visibility</small>
                                                        <p className="mb-0 text-muted">Not visible to students</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="d-flex align-items-center">
                                                    <div className="me-2" style={{ color: '#ffc107' }}>
                                                        <i className="bi bi-clock-history"></i>
                                                    </div>
                                                    <div>
                                                        <small className="fw-semibold">Action Required</small>
                                                        <p className="mb-0 text-muted">Complete within 7 days</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Dashboard Header */}
                        <div className="mb-4">
                            <h1 className="h3 fw-bold mb-1">Tutor Dashboard</h1>
                            <p className="text-muted mb-0">
                                Welcome back, {tutor?.full_name?.split(' ')[0]}! Manage your tutoring business here.
                            </p>
                        </div>

                        {/* Tutor Stats Cards */}
                        <div className="row g-3 mb-4">
                            <div className="col-md-4 col-sm-6">
                                <div className="card border-0 shadow-sm bg-primary bg-opacity-10">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h6 className="text-muted mb-1">Total Students</h6>
                                                <h3 className="fw-bold mb-0">{stats.totalStudents}</h3>
                                            </div>
                                            <i className="bi bi-people text-primary fs-2"></i>
                                        </div>
                                        <div className="mt-2">
                                            <small className="text-success">
                                                <i className="bi bi-arrow-up me-1"></i>
                                                +2 this week
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4 col-sm-6">
                                <div className="card border-0 shadow-sm bg-success bg-opacity-10">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h6 className="text-muted mb-1">Upcoming Sessions</h6>
                                                <h3 className="fw-bold mb-0">{stats.upcomingSessions}</h3>
                                            </div>
                                            <i className="bi bi-calendar-check text-success fs-2"></i>
                                        </div>
                                        <div className="mt-2">
                                            <small className="text-muted">Next 7 days</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4 col-sm-6">
                                <div className="card border-0 shadow-sm bg-warning bg-opacity-10">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h6 className="text-muted mb-1">Total Earnings</h6>
                                                <h3 className="fw-bold mb-0">₵{stats.totalEarnings}</h3>
                                            </div>
                                            <i className="bi bi-cash-coin text-warning fs-2"></i>
                                        </div>
                                        <div className="mt-2">
                                            <small className="text-success">
                                                <i className="bi bi-arrow-up me-1"></i>
                                                15% from last month
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4 col-sm-6">
                                <div className="card border-0 shadow-sm bg-info bg-opacity-10">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h6 className="text-muted mb-1">Completed Sessions</h6>
                                                <h3 className="fw-bold mb-0">{stats.completedSessions}</h3>
                                            </div>
                                            <i className="bi bi-check-circle text-info fs-2"></i>
                                        </div>
                                        <div className="mt-2">
                                            <small className="text-muted">All time</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4 col-sm-6">
                                <div className="card border-0 shadow-sm bg-purple bg-opacity-10">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h6 className="text-muted mb-1">Average Rating</h6>
                                                <h3 className="fw-bold mb-0">{stats.averageRating || "4.5"}</h3>
                                            </div>
                                            <i className="bi bi-star-fill text-warning fs-2"></i>
                                        </div>
                                        <div className="mt-2">
                                            <small className="text-muted">Based on {stats.completedSessions || 0} reviews</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4 col-sm-6">
                                <div className="card border-0 shadow-sm bg-danger bg-opacity-10">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h6 className="text-muted mb-1">Response Rate</h6>
                                                <h3 className="fw-bold mb-0">{stats.responseRate}%</h3>
                                            </div>
                                            <i className="bi bi-chat-dots text-danger fs-2"></i>
                                        </div>
                                        <div className="mt-2">
                                            <small className="text-muted">Last 30 days</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Tab Content */}
                        <div className="card border-0 shadow-sm">
                            <div className="card-body p-4">
                                {/* Overview Tab */}
                                {activeTab === 'overview' && (
                                    <div>
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <h4 className="fw-bold mb-0">Tutor Dashboard Overview</h4>
                                            <div className="d-flex gap-2">
                                                <button 
                                                    className="btn btn-primary"
                                                    onClick={() => navigate('/tutor/schedule')}
                                                >
                                                    <i className="bi bi-plus-circle me-2"></i>
                                                    Add Schedule
                                                </button>
                                                
                                            </div>
                                        </div>

                                        {/* Quick Stats Row */}
                                        <div className="row mb-5">
                                            <div className="col-md-6">
                                                <div className="card border h-100">
                                                    <div className="card-header bg-light">
                                                        <h6 className="fw-bold mb-0">
                                                            <i className="bi bi-clock-history me-2"></i>
                                                            Today's Sessions
                                                        </h6>
                                                    </div>
                                                    <div className="card-body">
                                                        {upcomingSessions.length > 0 ? (
                                                            upcomingSessions.filter(s => 
                                                                new Date(s.booking_date).toDateString() === new Date().toDateString()
                                                            ).map((session) => (
                                                                <div key={session.id} className="mb-3 pb-3 border-bottom">
                                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                                        <div>
                                                                            <h6 className="fw-bold mb-1">{session.subject}</h6>
                                                                            <p className="text-muted small mb-0">
                                                                                <i className="bi bi-person me-1"></i>
                                                                                {session.student_name}
                                                                            </p>
                                                                        </div>
                                                                        {getStatusBadge(session.status)}
                                                                    </div>
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <div>
                                                                            <span className="badge bg-light text-dark">
                                                                                <i className="bi bi-clock me-1"></i>
                                                                                {formatTime(session.booking_time)}
                                                                            </span>
                                                                            <span className="badge bg-light text-dark ms-2">
                                                                                <i className="bi bi-hourglass me-1"></i>
                                                                                {session.duration} hours
                                                                            </span>
                                                                        </div>
                                                                        <span className="fw-bold text-success">₵{session.amount}</span>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="text-center py-4">
                                                                <i className="bi bi-calendar-x display-4 text-muted mb-3"></i>
                                                                <p className="text-muted">No sessions scheduled for today</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="card border h-100">
                                                    <div className="card-header bg-light">
                                                        <h6 className="fw-bold mb-0">
                                                            <i className="bi bi-bell me-2"></i>
                                                            Pending Requests ({pendingRequests.length})
                                                        </h6>
                                                    </div>
                                                    <div className="card-body">
                                                        {pendingRequests.length > 0 ? (
                                                            pendingRequests.map((request) => (
                                                                <div key={request.id} className="mb-3 pb-3 border-bottom">
                                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                                        <div>
                                                                            <h6 className="fw-bold mb-1">{request.subject}</h6>
                                                                            <p className="text-muted small mb-0">
                                                                                <i className="bi bi-person me-1"></i>
                                                                                {request.student_name}
                                                                            </p>
                                                                        </div>
                                                                        {getStatusBadge(request.status)}
                                                                    </div>
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <div>
                                                                            <span className="badge bg-light text-dark">
                                                                                {formatDate(request.booking_date)}
                                                                            </span>
                                                                        </div>
                                                                        <div className="btn-group btn-group-sm">
                                                                            <button 
                                                                                className="btn btn-success btn-sm"
                                                                                onClick={() => handleBookingAction(request.id, 'confirm', 'confirmed')}
                                                                            >
                                                                                <i className="bi bi-check"></i>
                                                                            </button>
                                                                            <button 
                                                                                className="btn btn-danger btn-sm"
                                                                                onClick={() => handleBookingAction(request.id, 'decline', 'declined')}
                                                                            >
                                                                                <i className="bi bi-x"></i>
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="text-center py-4">
                                                                <i className="bi bi-check-circle display-4 text-success mb-3"></i>
                                                                <p className="text-muted">All requests handled</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Recent Earnings */}
                                        <div>
                                            <h5 className="fw-bold mb-3">
                                                <i className="bi bi-wallet2 me-2"></i>
                                                Recent Earnings
                                            </h5>
                                            {recentEarnings.length > 0 ? (
                                                <div className="table-responsive">
                                                    <table className="table table-hover">
                                                        <thead>
                                                            <tr>
                                                                <th>Date</th>
                                                                <th>Student</th>
                                                                <th>Subject</th>
                                                                <th>Duration</th>
                                                                <th>Amount</th>
                                                                <th>Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {recentEarnings.map((earning) => (
                                                                <tr key={earning.id}>
                                                                    <td>{formatDate(earning.booking_date)}</td>
                                                                    <td>{earning.student_name}</td>
                                                                    <td>{earning.subject}</td>
                                                                    <td>{earning.duration} hours</td>
                                                                    <td className="fw-bold text-success">₵{earning.earnings}</td>
                                                                    <td>{getStatusBadge(earning.status)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div className="text-center py-4">
                                                    <i className="bi bi-wallet display-4 text-muted mb-3"></i>
                                                    <p className="text-muted">No earnings yet. Start teaching to see earnings here.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Schedule Tab */}
                                {activeTab === 'schedule' && (
                                    <div>
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <h4 className="fw-bold mb-0">My Schedule</h4>
                                            <div className="btn-group">
                                                <button className="btn btn-outline-primary active">Week View</button>
                                                <button className="btn btn-outline-primary">Month View</button>
                                                <button className="btn btn-outline-primary">List View</button>
                                            </div>
                                        </div>

                                        {bookings.length > 0 ? (
                                            <div className="table-responsive">
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th>Date & Time</th>
                                                            <th>Student</th>
                                                            <th>Subject</th>
                                                            <th>Mode</th>
                                                            <th>Duration</th>
                                                            <th>Status</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {bookings
                                                            .sort((a, b) => new Date(a.booking_date) - new Date(b.booking_date))
                                                            .map((booking) => (
                                                                <tr key={booking.id}>
                                                                    <td>
                                                                        <div>{formatDate(booking.booking_date)}</div>
                                                                        <small className="text-muted">{formatTime(booking.booking_time)}</small>
                                                                    </td>
                                                                    <td>{booking.phone}</td>
                                                                    <td>{booking.subject}</td>
                                                                    <td>
                                                                        <span className={`badge ${booking.mode === 'online' ? 'bg-info' : 'bg-warning'}`}>
                                                                            {booking.mode}
                                                                        </span>
                                                                    </td>
                                                                    <td>{booking.duration} hours</td>
                                                                    <td>{getStatusBadge(booking.status)}</td>
                                                                    <td>
                                                                        <div className="btn-group btn-group-sm">
                                                                            <button 
                                                                                className="btn btn-outline-primary"
                                                                                onClick={() => {/* View booking details */}}
                                                                            >
                                                                                <i className="bi bi-eye"></i>
                                                                            </button>
                                                                            {booking.status === 'confirmed' && (
                                                                                <button 
                                                                                    className="btn btn-outline-danger"
                                                                                    onClick={() => handleBookingAction(booking.id, 'cancel', 'cancelled')}
                                                                                >
                                                                                    <i className="bi bi-x-circle"></i>
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <i className="bi bi-calendar-x display-4 text-muted mb-3"></i>
                                                <h5>No scheduled sessions</h5>
                                                <p className="text-muted mb-4">When students book sessions with you, they'll appear here.</p>
                                                
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Request Tab */}
                                {activeTab === 'request' && (
                                    <div>
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <h4 className="fw-bold mb-0">My Request</h4>
                                            
                                        </div>

                                        {bookings.length > 0 ? (
                                            <div className="table-responsive">
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th>Date & Time</th>
                                                            <th>Student</th>
                                                            <th>Subject</th>
                                                            <th>Mode</th>
                                                            <th>Duration</th>
                                                            <th>Status</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {bookings
                                                            .sort((a, b) => new Date(a.booking_date) - new Date(b.booking_date))
                                                            .map((booking) => (
                                                                <tr key={booking.id}>
                                                                    <td>
                                                                        <div>{formatDate(booking.booking_date)}</div>
                                                                        <small className="text-muted">{formatTime(booking.booking_time)}</small>
                                                                    </td>
                                                                    <td>{booking.phone}</td>
                                                                    <td>{booking.subject}</td>
                                                                    <td>
                                                                        <span className={`badge ${booking.mode === 'online' ? 'bg-info' : 'bg-warning'}`}>
                                                                            {booking.mode}
                                                                        </span>
                                                                    </td>
                                                                    <td>{booking.duration} hours</td>
                                                                    <td>{getStatusBadge(booking.status)}</td>
                                                                    <td>
                                                                        <div className="btn-group btn-group-sm">
                                                                            <button 
                                                                                className="btn btn-outline-primary"
                                                                                onClick={() => {/* View booking details */}}
                                                                            >
                                                                                <i className="bi bi-eye"></i>
                                                                            </button>
                                                                            {booking.status === 'confirmed' && (
                                                                                <button 
                                                                                    className="btn btn-outline-danger"
                                                                                    onClick={() => handleBookingAction(booking.id, 'cancel', 'cancelled')}
                                                                                >
                                                                                    <i className="bi bi-x-circle"></i>
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <i className="bi bi-calendar-x display-4 text-muted mb-3"></i>
                                                <h5>No request sessions</h5>
                                                <p className="text-muted mb-4">When students make request with you, they'll appear here.</p>
                                                
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Profile Tab */}
                                {activeTab === 'profile' && (
                                    <div>
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <h4 className="fw-bold mb-0">Tutor Profile Settings</h4>
                                            <button 
                                                className="btn btn-outline-primary"
                                                onClick={() => setEditMode(!editMode)}
                                            >
                                                {editMode ? (
                                                    <>
                                                        <i className="bi bi-x-circle me-2"></i>
                                                        Cancel Edit
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-pencil me-2"></i>
                                                        Edit Profile
                                                    </>
                                                )}
                                            </button>
                                        </div>

                                        {editMode ? (
                                            <form onSubmit={handleProfileUpdate}>
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <label className="form-label">Full Name *</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={profileForm.full_name}
                                                            onChange={(e) => setProfileForm({...profileForm, full_name: e.target.value})}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Email Address *</label>
                                                        <input
                                                            type="email"
                                                            className="form-control"
                                                            value={profileForm.email}
                                                            onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                                                            required
                                                            disabled
                                                        />
                                                        <small className="text-muted">Email cannot be changed</small>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Phone Number *</label>
                                                        <input
                                                            type="tel"
                                                            className="form-control"
                                                            value={profileForm.phone}
                                                            onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                                                            required
                                                            pattern="^(\+233|0)[0-9]{9}$"
                                                        />
                                                        <small className="text-muted">Format: +233XXXXXXXXX or 0XXXXXXXXX</small>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Hourly Rate (GHS) *</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={profileForm.hourly_rate}
                                                            onChange={(e) => setProfileForm({...profileForm, hourly_rate: e.target.value})}
                                                            required
                                                            min="20"
                                                            max="500"
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Qualification</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={profileForm.qualification}
                                                            onChange={(e) => setProfileForm({...profileForm, qualification: e.target.value})}
                                                            placeholder="e.g., M.Ed in Mathematics"
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Experience</label>
                                                        <select
                                                            className="form-select"
                                                            value={profileForm.experience}
                                                            onChange={(e) => setProfileForm({...profileForm, experience: e.target.value})}
                                                        >
                                                            <option value="">Select experience</option>
                                                            <option value="0-1">Less than 1 year</option>
                                                            <option value="1-3">1-3 years</option>
                                                            <option value="3-5">3-5 years</option>
                                                            <option value="5-10">5-10 years</option>
                                                            <option value="10+">10+ years</option>
                                                        </select>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Availability</label>
                                                        <select
                                                            className="form-select"
                                                            value={profileForm.availability}
                                                            onChange={(e) => setProfileForm({...profileForm, availability: e.target.value})}
                                                        >
                                                            <option value="">Select availability</option>
                                                            <option value="part-time">Part-time</option>
                                                            <option value="full-time">Full-time</option>
                                                            <option value="weekends">Weekends</option>
                                                            <option value="flexible">Flexible</option>
                                                        </select>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Subjects (comma separated)</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={profileForm.subjects}
                                                            onChange={(e) => setProfileForm({...profileForm, subjects: e.target.value})}
                                                            placeholder="e.g., Mathematics, Physics, Chemistry"
                                                        />
                                                    </div>
                                                    <div className="col-12">
                                                        <label className="form-label">Bio / Teaching Philosophy</label>
                                                        <textarea
                                                            className="form-control"
                                                            rows="4"
                                                            value={profileForm.bio}
                                                            onChange={(e) => setProfileForm({...profileForm, bio: e.target.value})}
                                                            placeholder="Tell students about your teaching approach and experience..."
                                                        ></textarea>
                                                    </div>
                                                    <div className="col-12 mt-4">
                                                        <div className="d-flex gap-2">
                                                            <button 
                                                                type="submit" 
                                                                className="btn btn-primary"
                                                            >
                                                                <i className="bi bi-check-circle me-2"></i>
                                                                Save Changes
                                                            </button>
                                                            <button 
                                                                type="button" 
                                                                className="btn btn-outline-secondary"
                                                                onClick={() => {
                                                                    setEditMode(false);
                                                                    setProfileForm({
                                                                        full_name: tutor?.full_name || "",
                                                                        email: tutor?.email || "",
                                                                        phone: tutor?.phone || "",
                                                                        profile_image: tutor?.profile_image || "",
                                                                        hourly_rate: tutor?.hourly_rate || "",
                                                                        qualification: tutor?.qualification || "",
                                                                        subjects: tutor?.subjects || "",
                                                                        experience: tutor?.experience || "",
                                                                        availability: tutor?.availability || "",
                                                                        bio: tutor?.teaching_philosophy || ""
                                                                    });
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </form>
                                        ) : (
                                            <div className="row">
                                                <div className="col-md-8">
                                                    <div className="mb-4">
                                                        <h6 className="text-muted mb-3">Tutor Information</h6>
                                                        <div className="p-3 bg-light rounded">
                                                            <div className="row mb-3">
                                                                <div className="col-4">
                                                                    <strong>Full Name:</strong>
                                                                </div>
                                                                <div className="col-8">
                                                                    {tutor?.full_name}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-4">
                                                                    <strong>Email:</strong>
                                                                </div>
                                                                <div className="col-8">
                                                                    {tutor?.email}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-4">
                                                                    <strong>Phone:</strong>
                                                                </div>
                                                                <div className="col-8">
                                                                    {tutor?.phone || 'Not provided'}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-4">
                                                                    <strong>Hourly Rate:</strong>
                                                                </div>
                                                                <div className="col-8">
                                                                    <span className="fw-bold text-success">₵{tutor?.hourly_rate || "50"}/hour</span>
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-4">
                                                                    <strong>Qualification:</strong>
                                                                </div>
                                                                <div className="col-8">
                                                                    {tutor?.qualification || 'Not specified'}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-4">
                                                                    <strong>Experience:</strong>
                                                                </div>
                                                                <div className="col-8">
                                                                    {tutor?.experience || 'Not specified'}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-4">
                                                                    <strong>Availability:</strong>
                                                                </div>
                                                                <div className="col-8">
                                                                    <span className="badge bg-info">{tutor?.availability || 'Flexible'}</span>
                                                                </div>
                                                            </div>
                                                            <div className="row">
                                                                <div className="col-4">
                                                                    <strong>Subjects:</strong>
                                                                </div>
                                                                <div className="col-8">
                                                                    <div className="d-flex flex-wrap gap-1">
                                                                        {parseSubjects(tutor?.subjects).map((subject, idx) => (
                                                                            <span key={idx} className="badge bg-primary bg-opacity-10 text-primary">
                                                                                {subject}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="mb-4">
                                                        <h6 className="text-muted mb-3">Teaching Philosophy</h6>
                                                        <div className="p-3 bg-light rounded">
                                                            <p>{tutor?.teaching_philosophy || 'No bio available'}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="mb-4">
                                                        <h6 className="text-muted mb-3">Account Statistics</h6>
                                                        <div className="p-3 bg-light rounded">
                                                            <div className="row mb-3">
                                                                <div className="col-8">
                                                                    <strong>Total Students:</strong>
                                                                </div>
                                                                <div className="col-4 text-end">
                                                                    {stats.totalStudents}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-8">
                                                                    <strong>Completed Sessions:</strong>
                                                                </div>
                                                                <div className="col-4 text-end">
                                                                    {stats.completedSessions}
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-8">
                                                                    <strong>Total Earnings:</strong>
                                                                </div>
                                                                <div className="col-4 text-end">
                                                                    <span className="fw-bold text-success">₵{stats.totalEarnings}</span>
                                                                </div>
                                                            </div>
                                                            <div className="row mb-3">
                                                                <div className="col-8">
                                                                    <strong>Average Rating:</strong>
                                                                </div>
                                                                <div className="col-4 text-end">
                                                                    <span className="fw-bold text-warning">{stats.averageRating || "4.5"}</span>
                                                                </div>
                                                            </div>
                                                            <div className="row">
                                                                <div className="col-8">
                                                                    <strong>Account Status:</strong>
                                                                </div>
                                                                <div className="col-4 text-end">
                                                                    <span className="badge bg-success">
                                                                        <i className="bi bi-patch-check me-1"></i>
                                                                        Verified
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="text-center">
                                                        <Link to="/verified-tutors" className="btn btn-outline-primary w-100 mb-2">
                                                            <i className="bi bi-eye me-2"></i>
                                                            View Public Profile
                                                        </Link>
                                                        <button className="btn btn-primary w-100" onClick={() => setEditMode(true)}>
                                                            <i className="bi bi-pencil me-2"></i>
                                                            Edit Profile
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />

            <style jsx>{`
                .nav-links {
                    padding: 0.75rem 1rem;
                    border-radius: 8px;
                    margin-bottom: 0.5rem;
                    transition: all 0.3s ease;
                    color: #495057;
                }
                
                .nav-links:hover {
                    background-color: #f8f9fa;
                    color: #0d6efd;
                }
                
                .nav-links.active {
                    background-color: rgba(13, 110, 253, 0.1);
                    color: #0d6efd;
                    font-weight: 500;
                }
                
                .card {
                    border-radius: 12px;
                    transition: transform 0.3s ease;
                }
                
                .card:hover {
                    transform: translateY(-2px);
                }
                
                .table th {
                    font-weight: 600;
                    color: #495057;
                    border-bottom: 2px solid #dee2e6;
                }
                
                .table td {
                    vertical-align: middle;
                }
                
                .badge {
                    padding: 0.35em 0.65em;
                    font-weight: 500;
                }
                
                .btn-group .btn.active {
                    background-color: #0d6efd;
                    color: white;
                    border-color: #0d6efd;
                }
                
                .bg-purple {
                    background-color: #6f42c1 !important;
                }
                
                .text-purple {
                    color: #6f42c1 !important;
                }
                
                .rating {
                    display: inline-flex;
                    align-items: center;
                }
                
                @media (max-width: 768px) {
                    .stats-cards .col-md-4 {
                        margin-bottom: 1rem;
                    }
                    
                    .table {
                        font-size: 0.875rem;
                    }
                    
                    .btn-group {
                        flex-wrap: wrap;
                    }
                    
                    .profile-preview {
                        width: 80px !important;
                        height: 80px !important;
                    }
                }
            `}</style>
        </>
    );
};

export default TutorDashboard;